import {
  Button,
  CancelOutlinedIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Text,
} from '@shared/components';
import { dayjs } from '@shared/utils';

import { formatThaiDate } from '../../utils/format-thai-date';

export interface RecurringExamEventCreationPartialSuccessModalProps {
  open: boolean;
  successCount: number;
  failedDates: Date[];
  onRetry: () => void;
  onClose: () => void;
}

function CountTile(props: Readonly<{ label: string; count: number; color: 'success' | 'error' }>) {
  const { label, count, color } = props;
  return (
    <Flex
      sx={(theme) => ({
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        gap: theme.spacing(1),
        minHeight: '96px',
        padding: theme.spacing(4),
        borderRadius: theme.shape.rounded(2),
        backgroundColor: theme.palette[color].lighter,
      })}
    >
      <Text sx={(theme) => ({ color: theme.palette[color].dark })}>{label}</Text>
      <Text variant='h3' sx={(theme) => ({ color: theme.palette[color].dark })}>
        {count} รอบ
      </Text>
    </Flex>
  );
}

export default function RecurringExamEventCreationPartialSuccessModal(
  props: Readonly<RecurringExamEventCreationPartialSuccessModalProps>,
) {
  const { open, successCount, failedDates, onRetry, onClose } = props;

  return (
    <Dialog open={open} maxWidth='sm'>
      <DialogTitle onClose={onClose} sx={(theme) => ({ padding: theme.spacing(6) })}>
        สร้างรอบสอบสำเร็จบางส่วน
      </DialogTitle>
      <Divider />
      <DialogContent sx={(theme) => ({ padding: theme.spacing(4, 6) })}>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <CountTile label='สร้างสำเร็จ' count={successCount} color='success' />
            <CountTile label='สร้างไม่สำเร็จ' count={failedDates.length} color='error' />
          </Flex>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
            <Text variant='body1Semibold'>รอบสอบที่สร้างไม่สำเร็จ ({failedDates.length} รอบ):</Text>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                padding: theme.spacing(4),
                borderRadius: theme.shape.rounded(2),
                backgroundColor: theme.palette.error.lighter,
              })}
            >
              {failedDates.map((failedDate) => (
                <Flex
                  key={failedDate.toISOString()}
                  sx={(theme) => ({
                    alignItems: 'center',
                    gap: theme.spacing(2),
                    padding: theme.spacing(2, 0),
                    borderBottom: `1px dashed ${theme.palette.error.light}`,
                    '&:last-of-type': { borderBottom: 'none' },
                  })}
                >
                  <CancelOutlinedIcon sx={(theme) => ({ color: theme.palette.error.main })} />
                  <Text
                    variant='body1Semibold'
                    sx={(theme) => ({ color: theme.palette.error.dark })}
                  >
                    {dayjs(failedDate).locale('th').format('dd')} {formatThaiDate(failedDate)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Text>
            กรุณากด{' '}
            <Text component='span' variant='body1Semibold'>
              &quot;ลองสร้างอีกครั้ง&quot;
            </Text>{' '}
            เพื่อสร้างรอบสอบที่เหลือให้ครบ หากปิดหน้าต่างไปก่อน
            คุณต้องกลับมาตรวจสอบและสร้างรอบสอบที่ขาดด้วยตนเองภายหลัง
          </Text>
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions sx={(theme) => ({ padding: theme.spacing(5, 6, 6), gap: theme.spacing(3) })}>
        <Button variant='outlined' color='secondary' size='large' onClick={onClose}>
          ปิดหน้าต่าง
        </Button>
        <Button size='large' onClick={onRetry}>
          ลองสร้างอีกครั้ง ({failedDates.length} รอบ)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
